import { useEffect, useRef, useState } from "react";
import { useRoute } from "wouter";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import { TimeInputField } from "@/components/foundations/inputs/TimeInputField/TimeInputField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { TaskLogEditor } from "@/components/tasks/details/TaskLogEditor/TaskLogEditor";
import { trpc } from "@/lib/trpc";
import { DEFAULT_AREA_COLOR, ListColorKey, resolveTaskColors } from "@/lib/listColors";
import { toast } from "sonner";
import { CircleCheckBig, CircleDot, Logs, NotepadText, Timer, X } from "lucide-react";
import { formatDateKey, roundTimeStringToStep } from "@/lib/time";

export function dateAndTimeToHHMM(d: Date | null | undefined) {
  if (!d) return "";
  const dt = new Date(d);
  return roundTimeStringToStep(
    `${dt.getHours().toString().padStart(2, "0")}:${dt
      .getMinutes()
      .toString()
      .padStart(2, "0")}`,
  );
}

export function combineDateAndTime(dateKey: string, hhmm: string): Date {
  const [y, mo, d] = dateKey.split("-").map(n => parseInt(n, 10));
  const [h, m] = hhmm.split(":").map(n => parseInt(n, 10));
  return new Date(y, (mo ?? 1) - 1, d ?? 1, h ?? 0, m ?? 0, 0, 0);
}

export function resultDurationMinutes(startedAt: Date | null | undefined, completedAt: Date | null | undefined) {
  if (!startedAt || !completedAt) return "";
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return "";
  return Math.round((end - start) / 60000).toString();
}

export function resultDurationFromTimes(dateKey: string, startedAtTime: string, endedAtTime: string) {
  if (!startedAtTime || !endedAtTime) return "";
  return resultDurationMinutes(
    combineDateAndTime(dateKey, startedAtTime),
    combineDateAndTime(dateKey, endedAtTime),
  );
}

export function endedAtFromDuration(dateKey: string, startedAtTime: string, durationMin: string) {
  if (!startedAtTime) return "";
  const raw = durationMin.trim();
  if (raw === "") return "";
  const minutes = Math.max(0, parseInt(raw, 10) || 0);
  const startedAt = combineDateAndTime(dateKey, startedAtTime);
  return dateAndTimeToHHMM(new Date(startedAt.getTime() + minutes * 60000));
}

export default function TaskResults() {
  const [, params] = useRoute<{ id: string }>("/task/:id/results");
  const id = params ? parseInt(params.id, 10) : 0;
  const utils = trpc.useUtils();
  const taskQuery = trpc.tasks.get.useQuery({ id }, { enabled: id > 0 });

  const [startedAtTime, setStartedAtTime] = useState("");
  const [endedAtTime, setEndedAtTime] = useState("");
  const [durationMin, setDurationMin] = useState("");
  const [notes, setNotes] = useState("");
  const [logText, setLogText] = useState("");
  const hydratedKey = useRef<number | null>(null);

  useEffect(() => {
    if (!taskQuery.data) return;
    if (hydratedKey.current === taskQuery.data.id) return;
    hydratedKey.current = taskQuery.data.id;
    const startedAt = taskQuery.data.startedAt as unknown as Date | null;
    const completedAt = taskQuery.data.completedAt as unknown as Date | null;
    setStartedAtTime(dateAndTimeToHHMM(startedAt));
    setEndedAtTime(dateAndTimeToHHMM(completedAt));
    setDurationMin(resultDurationMinutes(startedAt, completedAt));
    setNotes(taskQuery.data.notes ?? "");
    setLogText(taskQuery.data.log ?? "");
  }, [taskQuery.data]);

  const updateMutation = trpc.tasks.update.useMutation({
    onSuccess: () => {
      utils.tasks.get.invalidate({ id });
      utils.day.list.invalidate();
      utils.day.monthSummary.invalidate();
    },
    onError: e => toast.error(e.message),
  });

  function saveEndedAt() {
    if (!taskQuery.data) return;
    const next = endedAtTime ? combineDateAndTime(taskQuery.data.date ?? formatDateKey(new Date()), endedAtTime) : null;
    updateMutation.mutate({ id, completedAt: next });
  }

  function saveStartedAt() {
    if (!taskQuery.data) return;
    const next = startedAtTime ? combineDateAndTime(taskQuery.data.date ?? formatDateKey(new Date()), startedAtTime) : null;
    updateMutation.mutate({ id, startedAt: next });
  }

  function saveDuration() {
    if (!taskQuery.data || !startedAtTime) return;
    const raw = durationMin.trim();
    if (raw === "") return;
    const minutes = Math.max(0, parseInt(raw, 10) || 0);
    const startedAt = combineDateAndTime(taskQuery.data.date ?? formatDateKey(new Date()), startedAtTime);
    const completedAt = new Date(startedAt.getTime() + minutes * 60000);
    setEndedAtTime(dateAndTimeToHHMM(completedAt));
    updateMutation.mutate({ id, completedAt });
  }

  function clearStartedAt() {
    setStartedAtTime("");
    setDurationMin("");
    updateMutation.mutate({ id, startedAt: null });
  }

  function clearEndedAt() {
    setEndedAtTime("");
    setDurationMin("");
    updateMutation.mutate({ id, completedAt: null });
  }

  function clearDuration() {
    setDurationMin("");
    setEndedAtTime("");
    updateMutation.mutate({ id, completedAt: null });
  }

  function saveNotes() {
    if (!taskQuery.data) return;
    const next = notes.trim() === "" ? null : notes;
    if ((next ?? null) !== (taskQuery.data.notes ?? null)) {
      updateMutation.mutate({ id, notes: next });
    }
  }

  function saveLog() {
    if (!taskQuery.data) return;
    const next = logText.trim() === "" ? null : logText;
    if ((next ?? null) !== ((taskQuery.data.log ?? null) as string | null)) {
      updateMutation.mutate({ id, log: next });
    }
  }

  const searchParams = new URLSearchParams(
    typeof window === "undefined" ? "" : window.location.search,
  );
  const from = searchParams.get("from");
  const sheetTask = searchParams.get("sheetTask");
  const backTo =
    from && sheetTask
      ? `${from}${from.includes("?") ? "&" : "?"}sheetTask=${encodeURIComponent(sheetTask)}`
      : `/task/${id}`;

  if (!id) return null;
  if (taskQuery.isLoading || !taskQuery.data) {
    return (
      <AppShell backTo={backTo} title="Results">
        <div className="container max-w-2xl py-8">
          <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />
        </div>
      </AppShell>
    );
  }

  const t = taskQuery.data;
  const resultDate = t.date ?? formatDateKey(new Date());
  const statusColors = resolveTaskColors({
    barColor: (t.barColor ?? null) as ListColorKey | null,
    bgColor: (t.bgColor ?? null) as ListColorKey | null,
    listColor: (t.listColor as string | null) ?? DEFAULT_AREA_COLOR,
    listBgColor: (t as { listBgColor?: string | null }).listBgColor ?? null,
  });

  return (
    <AppShell backTo={backTo} title="Status Details">
      <div className="container max-w-2xl py-8 pb-12 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ResultField
            label="Started at"
            icon={<CircleDot className="h-3.5 w-3.5" />}
            accent={statusColors}
            onClear={startedAtTime ? clearStartedAt : undefined}
          >
            <TimeInputField
              value={startedAtTime}
              onChange={newTime => {
                setStartedAtTime(newTime);
                setDurationMin(resultDurationFromTimes(resultDate, newTime, endedAtTime));
                if (taskQuery.data) {
                  const next = newTime ? combineDateAndTime(taskQuery.data.date ?? formatDateKey(new Date()), newTime) : null;
                  updateMutation.mutate({ id, startedAt: next });
                }
              }}
              borderColor={statusColors.bar}
            />
          </ResultField>

          <ResultField
            label="Ended at"
            icon={<CircleCheckBig className="h-3.5 w-3.5" />}
            accent={statusColors}
            onClear={endedAtTime ? clearEndedAt : undefined}
          >
            <TimeInputField
              value={endedAtTime}
              onChange={newTime => {
                setEndedAtTime(newTime);
                setDurationMin(resultDurationFromTimes(resultDate, startedAtTime, newTime));
                if (taskQuery.data) {
                  const next = newTime ? combineDateAndTime(taskQuery.data.date ?? formatDateKey(new Date()), newTime) : null;
                  updateMutation.mutate({ id, completedAt: next });
                }
              }}
              borderColor={statusColors.bar}
            />
          </ResultField>

          <ResultField
            label="Duration"
            icon={<Timer className="h-3.5 w-3.5" />}
            accent={statusColors}
            onClear={durationMin ? clearDuration : undefined}
          >
            <Input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              min={0}
              value={durationMin}
              placeholder="optional"
              onChange={e => {
                const next = e.target.value;
                setDurationMin(next);
                const nextEndedAt = endedAtFromDuration(resultDate, startedAtTime, next);
                if (nextEndedAt) setEndedAtTime(nextEndedAt);
              }}
              onBlur={saveDuration}
            />
          </ResultField>
        </div>

        <NotesEditor
          value={notes}
          savedValue={taskQuery.data?.notes ?? ""}
          onChange={setNotes}
          onClear={() => setNotes("")}
          onCancel={() => setNotes(taskQuery.data?.notes ?? "")}
          onSave={saveNotes}
          isSaving={updateMutation.isPending}
          accent={statusColors}
        />

        <div
          className="bg-card border rounded-xl p-4 space-y-3"
          style={{
            borderColor: `color-mix(in oklch, ${statusColors.bar} 18%, transparent)`,
          }}
        >
          <TaskLogEditor
            value={logText}
            savedValue={taskQuery.data?.log ?? ""}
            onChange={setLogText}
            onClear={() => setLogText("")}
            onCancel={() => setLogText(taskQuery.data?.log ?? "")}
            onSave={saveLog}
            isSaving={updateMutation.isPending}
            accent={statusColors}
            headerIcon={<Logs className="h-3.5 w-3.5" />}
          />
        </div>
      </div>
    </AppShell>
  );
}

function NotesEditor({
  value,
  savedValue,
  onChange,
  onClear,
  onCancel,
  onSave,
  isSaving = false,
  accent,
}: {
  value: string;
  savedValue: string;
  onChange: (next: string) => void;
  onClear: () => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving?: boolean;
  accent: { bar: string; soft: string; ink: string };
}) {
  const hasChanges = value !== savedValue;

  return (
    <ResultField
      label="Notes"
      icon={<NotepadText className="h-3.5 w-3.5" />}
      accent={accent}
      onClear={value ? onClear : undefined}
    >
      <Textarea
        rows={3}
        value={value}
        placeholder="Private notes for this task..."
        onChange={e => onChange(e.target.value)}
      />

      {hasChanges && (
        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant="ghost"
            className="h-12 bg-muted/60 hover:bg-muted"
            onClick={onCancel}
            disabled={isSaving}
            style={{ color: accent.ink }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="h-12"
            onClick={onSave}
            disabled={isSaving}
            style={{ background: accent.soft, color: accent.bar }}
          >
            Save
          </Button>
        </div>
      )}
    </ResultField>
  );
}

export function ResultField({
  label,
  icon,
  accent,
  onClear,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  accent: { bar: string; ink: string };
  onClear?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="ui-result-field bg-card border rounded-xl p-4 space-y-2"
      style={{
        borderColor: `color-mix(in oklch, ${accent.bar} 18%, transparent)`,
      }}
    >
      <div className="ui-result-field__header flex items-center justify-between gap-3">
        <div
          className="ui-result-field__label inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"
          style={{ color: accent.ink, opacity: 0.68 }}
        >
          {icon}
          {label}
        </div>
        {onClear && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={onClear}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            style={{ color: accent.ink }}
            aria-label={`Clear ${label.toLowerCase()}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
